import {
  daysInclusiveDateOnly,
  formatDateDot,
  getIndividualPassLicenseWindowStartYear,
  PAID_PLAN_PARTIAL_DAY_RATE_WON,
} from './subscriptionPaidPlanPricing';
import { addDaysStartOfDay } from './groupPassCoupon';

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** 구매일 당일 포함 7일째 날(청약 철회·그룹 환불 안내 등) */
export function passRefundWindowEndInclusive(purchasedAt: Date): Date {
  return addDaysStartOfDay(purchasedAt, 6);
}

/** 결제일 기준 «결제 후 7일 이내»(당일 포함 7일째까지) */
function isWithinPurchaseWithdrawalWindow(purchasedAt: Date, now: Date): boolean {
  return startOfDay(now).getTime() <= passRefundWindowEndInclusive(purchasedAt).getTime();
}

/** 잔액(이용 공제 후)의 10% — 위약금(반올림, 원) */
function penaltyTenPercentOfWon(remainderAfterUsage: number): number {
  if (remainderAfterUsage <= 0) return 0;
  return Math.round(remainderAfterUsage * 0.1);
}

export interface PassRefundPreview {
  eligible: boolean;
  reason: string;
  refundWon: number;
  /** 청약 철회(7일 이내·미사용) 등으로 위약금·이용 공제 없이 전액 환불되는 경우 */
  fullRefundNoFee: boolean;
  usageDeductionWon: number;
  feeWon: number;
  lines: string[];
}

function buildPreview(p: {
  eligible: boolean;
  reason: string;
  refundWon: number;
  fullRefundNoFee: boolean;
  usageDeductionWon: number;
  feeWon: number;
  lines: string[];
}): PassRefundPreview {
  return { ...p };
}

export type IndividualPassRefundOrderPeer = {
  id: string;
  refundedAt?: Date;
  periodStart: Date;
  periodEnd: Date;
};

/**
 * 같은 이용권 기간(2월~다음 해 2월) 안에서, 캘린더상 더 앞선 이용 구간이 남아 있으면
 * 그 구간을 먼저 환불해야만 이 주문을 환불할 수 있습니다.
 */
export function getIndividualPassRefundOrderBlockMessage(
  record: IndividualPassRefundOrderPeer,
  nonRefundedPeers: IndividualPassRefundOrderPeer[]
): string | null {
  if (record.refundedAt) return null;
  const y0 = getIndividualPassLicenseWindowStartYear(record.periodStart);
  const pool = nonRefundedPeers.filter(
    (r) => !r.refundedAt && getIndividualPassLicenseWindowStartYear(r.periodStart) === y0
  );
  if (pool.length <= 1) return null;
  const sorted = [...pool].sort((a, b) => {
    const da = startOfDay(a.periodStart).getTime();
    const db = startOfDay(b.periodStart).getTime();
    if (da !== db) return da - db;
    return startOfDay(a.periodEnd).getTime() - startOfDay(b.periodEnd).getTime();
  });
  const mustRefundFirst = sorted[0]!;
  if (mustRefundFirst.id === record.id) return null;
  return (
    `같은 이용권 기간(매년 2월~다음 해 2월)에서, 더 앞선 이용 구간(${formatDateDot(mustRefundFirst.periodStart)}~${formatDateDot(mustRefundFirst.periodEnd)})을 먼저 환불한 뒤에 이 주문을 환불할 수 있습니다. ` +
    `중간·뒤 구간만 먼저 환불하면 2월 말까지 유료로 이어 쓰는 규칙과 맞지 않아, 앞선 구간부터 순서대로 환불되도록 합니다.`
  );
}

/**
 * 개별 이용권 환불 예상액(표준 정책 반영).
 * - **청약 철회**: 결제 후 7일 이내·미사용 → 결제액 **100%** 환불.
 * - **미사용 환불**: 결제 후 7일 경과·미사용 → 결제액의 **90%** 환불(10% 위약금).
 * - **중도 해지**: 이용(과금) 시작 후 → 결제액 − (실제 사용 일수 × 일당) − **잔액의 10%** 위약금.
 */
export function computeIndividualPassRefundPreview(
  record: {
    id?: string;
    refundedAt?: Date;
    purchasedAt: Date;
    periodStart: Date;
    periodEnd: Date;
    amountWon: number;
  },
  now: Date = new Date(),
  /** 미환불 개별 이용권 전체(본인 포함). 넘기면 환불 순서(앞선 구간 우선)를 검사합니다. */
  nonRefundedIndividualPeers?: IndividualPassRefundOrderPeer[]
): PassRefundPreview {
  if (record.refundedAt) {
    return buildPreview({
      eligible: false,
      reason: '이미 환불 처리된 주문입니다.',
      refundWon: 0,
      fullRefundNoFee: false,
      usageDeductionWon: 0,
      feeWon: 0,
      lines: ['이 주문은 환불이 완료된 상태입니다.'],
    });
  }

  if (nonRefundedIndividualPeers && record.id) {
    const orderMsg = getIndividualPassRefundOrderBlockMessage(record as IndividualPassRefundOrderPeer, nonRefundedIndividualPeers);
    if (orderMsg) {
      return buildPreview({
        eligible: false,
        reason: orderMsg,
        refundWon: 0,
        fullRefundNoFee: false,
        usageDeductionWon: 0,
        feeWon: 0,
        lines: [orderMsg],
      });
    }
  }

  const ps = startOfDay(record.periodStart);
  const pe = startOfDay(record.periodEnd);
  const n0 = startOfDay(now);

  let usedDays = 0;
  if (n0.getTime() >= ps.getTime()) {
    const through = n0.getTime() <= pe.getTime() ? n0 : pe;
    usedDays = daysInclusiveDateOnly(ps, through);
  }

  const within7 = isWithinPurchaseWithdrawalWindow(record.purchasedAt, now);

  let refundWon = 0;
  let usageDeductionWon = 0;
  let feeWon = 0;
  let fullRefundNoFee = false;
  const lines: string[] = [];

  if (usedDays > 0) {
    usageDeductionWon = Math.min(record.amountWon, usedDays * PAID_PLAN_PARTIAL_DAY_RATE_WON);
    const afterUsage = Math.max(0, record.amountWon - usageDeductionWon);
    // 7일 이내: 사용일 공제만 / 7일 경과: 잔액의 10% 수수료 추가
    feeWon = within7 ? 0 : penaltyTenPercentOfWon(afterUsage);
    refundWon = Math.max(0, afterUsage - feeWon);
    fullRefundNoFee = within7 && usageDeductionWon === 0 && record.amountWon > 0;
    lines.push(
      within7
        ? '구분: 중도 해지(결제 후 7일 이내·사용)'
        : '구분: 중도 해지(결제 후 7일 경과·사용)',
      `이용 기간: ${record.periodStart.toLocaleDateString('ko-KR')} ~ ${record.periodEnd.toLocaleDateString('ko-KR')}`,
      `이용일수(오늘 기준, 이용 시작일 포함): ${usedDays}일`,
      `실제 사용 일수 금액: ${usageDeductionWon.toLocaleString('ko-KR')}원(1일 ${PAID_PLAN_PARTIAL_DAY_RATE_WON.toLocaleString('ko-KR')}원)`,
      `잔액: ${afterUsage.toLocaleString('ko-KR')}원`,
      ...(within7
        ? ([] as string[])
        : [`위약금(잔액의 10%): ${feeWon.toLocaleString('ko-KR')}원`]),
      `예상 환불액: ${refundWon.toLocaleString('ko-KR')}원`
    );
  } else if (within7) {
    refundWon = record.amountWon;
    usageDeductionWon = 0;
    feeWon = 0;
    fullRefundNoFee = record.amountWon > 0;
    lines.push(
      '구분: 청약 철회(결제 후 7일 이내·미사용)',
      `결제액 ${record.amountWon.toLocaleString('ko-KR')}원 → 전액 환불(100%)`,
      `예상 환불액: ${refundWon.toLocaleString('ko-KR')}원`
    );
  } else {
    feeWon = Math.round(record.amountWon * 0.1);
    refundWon = Math.max(0, record.amountWon - feeWon);
    usageDeductionWon = 0;
    fullRefundNoFee = false;
    lines.push(
      '구분: 미사용 환불(결제 후 7일 경과·미사용)',
      `결제액 ${record.amountWon.toLocaleString('ko-KR')}원`,
      `위약금(10%): ${feeWon.toLocaleString('ko-KR')}원`,
      `예상 환불액(90%): ${refundWon.toLocaleString('ko-KR')}원`
    );
  }

  if (refundWon <= 0) {
    return buildPreview({
      eligible: false,
      reason: '공제 후 환불 가능 금액이 0원입니다.',
      refundWon: 0,
      fullRefundNoFee: false,
      usageDeductionWon,
      feeWon,
      lines,
    });
  }

  return buildPreview({
    eligible: true,
    reason: '',
    refundWon,
    fullRefundNoFee,
    usageDeductionWon,
    feeWon,
    lines,
  });
}

/**
 * 유료·무료 이벤트 이용권 환불.
 * - 무료(`refundDisabled`): 환불 절차 없음.
 * - 유료: 개별 이용권과 동일(사용일×일당 공제, 7일 경과 시 잔액 10% 수수료).
 */
export function computeEventPassRefundPreview(
  record: {
    id?: string;
    kind?: 'paid' | 'free';
    refundDisabled?: boolean;
    refundedAt?: Date;
    purchasedAt: Date;
    periodStart: Date;
    periodEnd: Date;
    amountWon: number;
  },
  now: Date = new Date()
): PassRefundPreview {
  if (record.refundDisabled || record.kind === 'free') {
    return buildPreview({
      eligible: false,
      reason: '무료 이벤트 이용권은 환불 절차가 없습니다.',
      refundWon: 0,
      fullRefundNoFee: false,
      usageDeductionWon: 0,
      feeWon: 0,
      lines: ['해당 이용권은 환불 절차가 없습니다.'],
    });
  }
  return computeIndividualPassRefundPreview(record, now);
}

/**
 * 그룹 이용권 환불 예상액(개별과 동일한 표준 정책).
 * - **청약 철회**: 결제 후 7일 이내·활성 이용권 없음(미사용) → 결제액 **100%** 환불.
 * - **미사용 환불**: 결제 후 7일 경과·활성 이용권 없음 → 결제액의 **90%** 환불(10% 위약금).
 * - **중도 해지**: 활성 이용권 있음 → 결제액 − (활성 이용권 사용일 × 일당) − **잔액의 10%** 위약금.
 *   (일당 공제 합은 활성 이용권 결제분 `totalWon×activated/quantity` 한도.)
 */
export function computeGroupPassRefundPreview(
  record: {
    refundedAt?: Date;
    purchasedAt: Date;
    refundPeriodEnd: Date;
    quantity: number;
    activatedCount: number;
    totalWon: number;
    activationPeriodStart: Date;
    activationPeriodEnd: Date;
  },
  now: Date = new Date()
): PassRefundPreview {
  if (record.refundedAt) {
    return buildPreview({
      eligible: false,
      reason: '이미 환불 처리된 주문입니다.',
      refundWon: 0,
      fullRefundNoFee: false,
      usageDeductionWon: 0,
      feeWon: 0,
      lines: ['이 주문은 환불이 완료된 상태입니다.'],
    });
  }

  const q = Math.max(1, record.quantity);
  const activated = Math.min(record.activatedCount, q);
  const within7 = isWithinPurchaseWithdrawalWindow(record.purchasedAt, now);

  let refundWon = 0;
  let usageDeductionWon = 0;
  let feeWon = 0;
  let fullRefundNoFee = false;
  const lines: string[] = [];

  if (activated === 0) {
    if (within7) {
      refundWon = record.totalWon;
      fullRefundNoFee = record.totalWon > 0;
      lines.push(
        '구분: 청약 철회(결제 후 7일 이내·미사용)',
        `결제액 ${record.totalWon.toLocaleString('ko-KR')}원 → 전액 환불(100%)`,
        `예상 환불액: ${refundWon.toLocaleString('ko-KR')}원`
      );
    } else {
      feeWon = Math.round(record.totalWon * 0.1);
      refundWon = Math.max(0, record.totalWon - feeWon);
      fullRefundNoFee = false;
      lines.push(
        '구분: 미사용 환불(결제 후 7일 경과·활성 이용권 없음)',
        `결제액 ${record.totalWon.toLocaleString('ko-KR')}원`,
        `위약금(10%): ${feeWon.toLocaleString('ko-KR')}원`,
        `예상 환불액(90%): ${refundWon.toLocaleString('ko-KR')}원`
      );
    }
  } else {
    const ps = startOfDay(record.activationPeriodStart);
    const pe = startOfDay(record.activationPeriodEnd);
    const n0 = startOfDay(now);

    let usedDaysPerSeat = 0;
    if (n0.getTime() >= ps.getTime()) {
      const through = n0.getTime() <= pe.getTime() ? n0 : pe;
      usedDaysPerSeat = daysInclusiveDateOnly(ps, through);
    }

    const rawUsageDeductionWon = activated * usedDaysPerSeat * PAID_PLAN_PARTIAL_DAY_RATE_WON;
    const maxDeductionForActiveSeats = Math.min(
      record.totalWon,
      Math.round((record.totalWon * activated) / q)
    );
    usageDeductionWon = Math.min(maxDeductionForActiveSeats, rawUsageDeductionWon);
    const afterUsage = Math.max(0, record.totalWon - usageDeductionWon);
    feeWon = penaltyTenPercentOfWon(afterUsage);
    refundWon = Math.max(0, afterUsage - feeWon);
    fullRefundNoFee = false;

    lines.push(
      '구분: 중도 해지(활성 이용권·사용 후)',
      `이용권: 활성 ${activated}명 / 구매 ${q}명`,
      `활성 이용권 이용 기간(날짜만): ${formatDateDot(ps)} ~ ${formatDateDot(pe)}`,
      `활성 이용권당 이용일수(오늘 기준, 시작일 포함): ${usedDaysPerSeat}일`,
      `실제 사용 일수 금액: ${usageDeductionWon.toLocaleString('ko-KR')}원(1일 ${PAID_PLAN_PARTIAL_DAY_RATE_WON.toLocaleString('ko-KR')}원, 활성 결제분 한도 ${maxDeductionForActiveSeats.toLocaleString('ko-KR')}원)`,
      `잔액: ${afterUsage.toLocaleString('ko-KR')}원`,
      `위약금(잔액의 10%): ${feeWon.toLocaleString('ko-KR')}원`,
      `예상 환불액: ${refundWon.toLocaleString('ko-KR')}원`
    );
  }

  if (refundWon <= 0) {
    return buildPreview({
      eligible: false,
      reason: '공제 후 환불 가능 금액이 0원입니다.',
      refundWon: 0,
      fullRefundNoFee: false,
      usageDeductionWon,
      feeWon,
      lines,
    });
  }

  return buildPreview({
    eligible: true,
    reason: '',
    refundWon,
    fullRefundNoFee,
    usageDeductionWon,
    feeWon,
    lines,
  });
}
